import React, { useContext, useEffect, useState } from 'react';
import { ExamData, ResponsesData, SelectedQuestion } from '../context';
import LiveExam from '../../../../Services/liveexam.services';
import { defaultTheme, withAlpha, PaletteBtn } from '../../../../components/nx';

const t = defaultTheme;

interface IExamSidebar {
    questionIndex: any;
    sectionData: any;
}

export default function ExamSidebar({ questionIndex, sectionData }: IExamSidebar) {
    const { selectedQuestionId, setSelectedQuestionId } = useContext<any>(SelectedQuestion);
    const [sideBarExpand, setSideBarExpand] = useState(true);
    const examData: any = useContext(ExamData);

    const [questionStatus, setQuestionStatus] = useState<any>({
        markedForReview: [],
        answered: [],
        markedandAnswered: [],
    });
    const [purpleQ, setPurpleQ] = useState<any>([]);
    const [greenQ, setGreenQ] = useState<any>([]);
    const [unviewedQ, setUnviewedQ] = useState<any>([]);

    const handleQuestionChange = (questionId: any) => {
        setSelectedQuestionId({ ...selectedQuestionId, question: questionId });
    };

    const checkifAnswered = (optionData: any) => {
        if (optionData.length <= 0) return false;
        return optionData.find((data: any) => data.value) ? true : false;
    };

    const getAllUserMarkedResponses = async () => {
        try {
            const res = await LiveExam.getAllUserEncryptedOptions();
            if (res.status === 200) {
                let markedForReview: any[] = [];
                let answered: any[] = [];
                let markedandAnswered: any[] = [];

                for (let enc_option of res.data.data) {
                    const questionExistInSection = sectionData?.questions?.find(
                        (question_data: any) => question_data === enc_option.questionId
                    );
                    if (!questionExistInSection) continue;

                    if (enc_option.markedForReview === true) {
                        if (enc_option.selectedOption?.length > 0 && checkifAnswered(enc_option.selectedOption)) {
                            markedandAnswered.push(enc_option);
                        } else {
                            markedForReview.push(enc_option);
                        }
                    } else {
                        if (checkifAnswered(enc_option.selectedOption)) {
                            answered.push(enc_option);
                        }
                    }
                }
                setQuestionStatus({ markedForReview, answered, markedandAnswered });
            }
        } catch (e) {}
    };

    function getStartingIndex() {
        let numToAdd = 0;
        for (let i = 0; i < examData?.data?.length; i++) {
            if (sectionData?.section === examData?.data[i]._id) return numToAdd;
            if (examData?.data[i].questionData) numToAdd += examData?.data[i].questionData.length;
        }
        return numToAdd;
    }

    const getQuestionStatus = (questionId: string): 'unanswered' | 'answered' | 'review' | 'both' | 'approved' | 'remarked' => {
        if (greenQ.includes(questionId)) return 'approved';
        if (purpleQ.includes(questionId)) return 'remarked';
        if (questionStatus.markedandAnswered.find((q: any) => q.questionId === questionId)) return 'both';
        if (questionStatus.answered.find((q: any) => q.questionId === questionId)) return 'answered';
        if (questionStatus.markedForReview.find((q: any) => q.questionId === questionId)) return 'review';
        return 'unanswered';
    };

    async function getRemarkStatuses() {
        try {
            const res = await LiveExam.getpurpleQuestion();
            if (res.status === 200) {
                setPurpleQ(res.data?.data?.purpleQuestions || []);
                setGreenQ(res.data?.data?.greenQuestions || []);
                setUnviewedQ(res.data?.data?.unviewedQuestions || []);
            }
        } catch (e) {}
    }

    useEffect(() => {
        if (sectionData?.questions) getAllUserMarkedResponses();
    }, [questionIndex, sectionData]);

    useEffect(() => { getRemarkStatuses(); }, [selectedQuestionId?.question]);

    const calculateProgress = () => {
        const totalQuestions = sectionData?.questions?.length || 0;
        const totalAnswered = questionStatus.answered.length + questionStatus.markedandAnswered.length;
        return totalQuestions > 0 ? Number(((totalAnswered / totalQuestions) * 100).toFixed(0)) : 0;
    };

    const progress = calculateProgress();

    if (!sideBarExpand) {
        return (
            <aside style={{
                width: 52, borderRight: `1px solid ${t.border}`, background: t.surfaceAlt,
                display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px 0', overflow: 'auto',
            }}>
                <span
                    onClick={() => setSideBarExpand(true)}
                    style={{ fontSize: 10, color: t.inkSoft, cursor: 'pointer', marginBottom: 12, writingMode: 'vertical-rl', fontWeight: 500 }}
                >
                    Expand →
                </span>
                {sectionData?.questions?.map((questionId: string, index: number) => (
                    <div
                        key={questionId}
                        onClick={() => handleQuestionChange(questionId)}
                        style={{
                            width: 28, height: 28, borderRadius: 6,
                            fontSize: 11, fontWeight: 500, cursor: 'pointer', marginBottom: 4,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            position: 'relative',
                            background: getQuestionStatus(questionId) === 'approved' ? withAlpha(t.success, 0.14)
                                : getQuestionStatus(questionId) === 'remarked' ? withAlpha('#dc3545', 0.12)
                                : getQuestionStatus(questionId) === 'answered' ? withAlpha('#0d6efd', 0.12)
                                : getQuestionStatus(questionId) === 'review' ? withAlpha(t.warning, 0.16)
                                : getQuestionStatus(questionId) === 'both' ? withAlpha(t.accent, 0.14)
                                : 'transparent',
                            color: selectedQuestionId?.question === questionId ? t.ink : t.inkSoft,
                            border: selectedQuestionId?.question === questionId ? `1.5px solid ${t.ink}` : `1px solid ${t.border}`,
                            fontFamily: '"Geist Mono", ui-monospace, monospace',
                        }}
                    >
                        {index + 1 + getStartingIndex()}
                        {unviewedQ.includes(questionId) && (
                            <span style={{
                                position: 'absolute', top: -2, right: -2,
                                width: 6, height: 6, borderRadius: 99,
                                background: '#e53e3e',
                            }} />
                        )}
                    </div>
                ))}
            </aside>
        );
    }

    return (
        <aside style={{
            width: 260, borderRight: `1px solid ${t.border}`, padding: 18,
            background: t.surfaceAlt, display: 'flex', flexDirection: 'column', gap: 14, overflow: 'auto',
            fontFamily: '"Geist", ui-sans-serif, system-ui, -apple-system, sans-serif',
        }}>
            {/* Progress */}
            <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600 }}>Section progress</span>
                    <span style={{ fontSize: 11, color: t.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
                        {questionStatus.answered.length + questionStatus.markedandAnswered.length} / {sectionData?.questions?.length || 0}
                    </span>
                </div>
                <div style={{ height: 4, background: t.paperAlt, borderRadius: 99, overflow: 'hidden', border: `1px solid ${t.border}` }}>
                    <div style={{ width: `${progress}%`, height: '100%', background: t.accent, transition: 'width 0.3s ease' }} />
                </div>
            </div>

            <span
                onClick={() => setSideBarExpand(false)}
                style={{ fontSize: 11, color: t.inkSoft, cursor: 'pointer', fontWeight: 500 }}
            >
                ← Retract
            </span>

            {/* Question grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                {sectionData?.questions?.map((questionId: string, index: number) => (
                    <PaletteBtn
                        key={questionId}
                        n={index + 1 + getStartingIndex()}
                        status={getQuestionStatus(questionId)}
                        t={t}
                        active={selectedQuestionId?.question === questionId}
                        onClick={() => handleQuestionChange(questionId)}
                        hasNotification={unviewedQ.includes(questionId)}
                    />
                ))}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8, borderTop: `1px dashed ${t.border}` }}>
                <div style={{ fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600 }}>Legend</div>
                {[
                    { k: 'unanswered', label: 'Not answered', bg: t.dark ? t.surfaceAlt : '#f0eee9', border: t.border },
                    { k: 'answered', label: 'Answered', bg: withAlpha('#0d6efd', 0.12), border: withAlpha('#0d6efd', 0.4) },
                    { k: 'review', label: 'Marked for review', bg: withAlpha(t.warning, 0.16), border: withAlpha(t.warning, 0.35) },
                    { k: 'both', label: 'Answered & marked', bg: withAlpha(t.accent, 0.14), border: withAlpha(t.accent, 0.4) },
                    { k: 'approved', label: 'Approved', bg: withAlpha(t.success, 0.14), border: withAlpha(t.success, 0.35) },
                    { k: 'remarked', label: 'Needs attention', bg: withAlpha('#dc3545', 0.12), border: withAlpha('#dc3545', 0.35) },
                    { k: 'notification', label: 'New activity', bg: '#e53e3e', border: '#e53e3e', dot: true },
                ].map((p: any) => (
                    <div key={p.k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: t.inkSoft }}>
                        <span style={{
                            width: p.dot ? 8 : 14, height: p.dot ? 8 : 14,
                            borderRadius: p.dot ? 99 : 4,
                            background: p.bg, border: `1px solid ${p.border}`, flexShrink: 0,
                            ...(p.dot ? { boxShadow: '0 0 0 1px #e53e3e' } : {}),
                        }} />
                        {p.label}
                    </div>
                ))}
            </div>
        </aside>
    );
}
